/**
 * Which objective the learner is working on, published for the one thing that
 * is not on the level screen: the chat.
 *
 * ## Why this is a module and not a prop
 *
 * A level room is worth being in because the people in it are stuck on the
 * same thing you are, and "three people here are on objective 2" is the
 * sentence that turns a list of names into somebody to ask. The number that
 * makes that sentence lives in `Play`, deep inside the level screen. The panel
 * that needs it is `ChatDock`, which is mounted beside `Play` rather than
 * inside it, because the bubble is on the boot screen and the level list too.
 *
 * The obvious repair — lift the number to `App` and hand it down both
 * branches — is the wrong one, and not for reasons of taste. `App` re-renders
 * the whole product, the terminal and the editor included, and this number
 * changes on every grade. Paying a full re-render of a running VM's frame so
 * that a four-word line in a member list is current is a bad trade, and it is
 * the kind of bad trade that is invisible until a slow machine makes it
 * visible.
 *
 * So it is a store of exactly one number, with `useSyncExternalStore` at the
 * reading end. React subscribes the one component that cares, `Play` writes on
 * a change, and nothing between them renders at all.
 *
 * ## What the number means
 *
 * The index of the first objective that is neither passed nor skipped — what
 * a learner would point at if you asked what they were doing. Null means there
 * is no such thing: the map, a sandbox level, a level that has never been
 * graded, or one that is finished. Null is the honest answer in all four, and
 * the member list draws nothing rather than a guess.
 *
 * It is a hint about a person and never a fact about their progress. It is not
 * signed, not merged, not stored, and not on the progress record; it is one
 * small integer that goes out with a `me` frame and is forgotten when the
 * socket closes. The protocol's `cleanObjective` bounds it on the way in, on
 * the principle that a number from a browser is a number from a stranger.
 */

/** The current objective index, or null. Read through {@link objectiveSnapshot}. */
let objective: number | null = null;

/** Everyone listening. A Set, so unsubscribing is cheap and idempotent. */
const listeners = new Set<() => void>();

/**
 * Say which objective is being worked on now.
 *
 * A no-op when nothing changed, which matters more than it looks: `Play`
 * re-renders on every keystroke in the editor, and a store that notified on
 * every render would have cost more than the prop it replaced.
 */
export function setObjective(next: number | null): void {
  if (next === objective) return;
  objective = next;
  for (const listener of listeners) listener();
}

/** The current value. The snapshot half of `useSyncExternalStore`. */
export function objectiveSnapshot(): number | null {
  return objective;
}

/** The subscribe half. Returns the unsubscribe, as React asks. */
export function subscribeObjective(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The first objective that is neither passed nor skipped, or null when there
 * is not one.
 *
 * Kept here beside the store rather than in `Play` so that the definition of
 * "which objective someone is on" is written down once. `undefined` in, null
 * out: a level that has never been graded has no objectives to be on, and a
 * level whose objectives are all passed is finished rather than at the end.
 */
export function currentObjective(objectives: readonly { passed: boolean; skipped?: boolean }[] | undefined): number | null {
  if (!objectives || objectives.length === 0) return null;
  const at = objectives.findIndex((o) => !o.passed && !o.skipped);
  return at === -1 ? null : at;
}
